"use client"

import { memo, useRef, useEffect, Children, cloneElement, isValidElement, useMemo, useState } from "react"
import type { ReactNode, ReactElement } from "react"
import { cn } from "@/lib/utils"
import { useSidebarLiquidGlass } from "@/hooks/use-sidebar-liquid-glass"
import { useTooltipDirection } from "./hooks/useTooltipDirection"
import { TooltipTitleCylinder } from "./TooltipTitleCylinder"
import '@/animations/tooltip-keyframes.css'

/**
 * Helper: Extraer key única de un child (usa href del SidebarSubLink)
 */
function getChildKey(child: ReactElement<any>, index: number): string {
  // Prioridad: key explícita > href > index
  if (child.key) return String(child.key)
  if (child.props?.href) return child.props.href
  return `item-${index}`
}

export interface SidebarTooltipProps {
  /** Contenido del tooltip */
  children: ReactNode
  /** Título opcional del tooltip */
  title?: string
  /** Modo debug para visualizar safety triangle y layers */
  debug?: boolean
  /** Handler llamado cuando el mouse sale del tooltip */
  onMouseLeave?: () => void

  /** Habilitar liquid glass con SVG displacement filters (false = backdrop-blur básico) */
  enableLiquidGlass?: boolean
  /** Habilitar Edge Glass 3D (dual backdrop layers para profundidad) */
  enableEdgeGlass3D?: boolean
  /** Habilitar Backdrop Extension (Josh Comeau technique - 200% height) */
  enableBackdropExtension?: boolean
  /** Habilitar chromatic aberration RGB (requiere enableLiquidGlass) */
  enableChromaticAberration?: boolean

  /** Habilitar animaciones staggered de items (entrada/salida escalonada) */
  enableTooltipItemAnimations?: boolean
  /** Habilitar animación 3D de cilindro para el título del tooltip */
  enableCylinderTitle?: boolean
}

export const SidebarTooltip = memo(function SidebarTooltip({
  children,
  title,
  debug = false,
  onMouseLeave,
  enableLiquidGlass = false,
  enableEdgeGlass3D = false,
  enableBackdropExtension = false,
  enableChromaticAberration = false,
  enableTooltipItemAnimations = true,
  enableCylinderTitle = false
}: SidebarTooltipProps) {
  const tooltipRef = useRef<HTMLDivElement>(null)
  const contentGridRef = useRef<HTMLDivElement>(null)
  const previousChildrenKeysRef = useRef<Set<string>>(new Set())
  const previousTitleRef = useRef<string | undefined>(undefined)
  const isFirstRenderRef = useRef(true)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const { getDirection } = useTooltipDirection()

  // Extraer keys actuales de children
  const currentChildrenKeys = useMemo(() => {
    const keys = new Set<string>()
    Children.forEach(children, (child, index) => {
      if (isValidElement(child)) {
        keys.add(getChildKey(child as ReactElement<any>, index))
      }
    })
    return keys
  }, [children])

  // Detectar si hubo cambio en el título
  const titleChanged = previousTitleRef.current !== undefined && previousTitleRef.current !== title

  // Determinar dirección de navegación (usamos el primer item como referencia)
  const currentDirection = useMemo(() => {
    const firstChild = Children.toArray(children)[0]
    if (isValidElement(firstChild)) {
      return getDirection(0)
    }
    return 'down'
  }, [children, getDirection])

  // Procesar children para agregar animationOrder y animationState
  const processedChildren = useMemo(() => {
    const prevKeys = previousChildrenKeysRef.current
    const isFirstRender = isFirstRenderRef.current

    return Children.map(children, (child, index) => {
      if (!isValidElement(child)) return child

      const childKey = getChildKey(child as ReactElement<any>, index)
      const isNew = !prevKeys.has(childKey)

      // Determinar estado de animación
      let animationState: 'entering' | 'leaving' | 'static' = 'static'

      if (enableTooltipItemAnimations) {
        if (isFirstRender) {
          // Primera renderización - todos entran con stagger
          animationState = 'entering'
        } else if (isNew) {
          // Item nuevo después del primer render
          animationState = 'entering'
        }
        // Items que ya existían se quedan en 'static'
      }

      // Clonar elemento agregando props de animación
      return cloneElement(child as ReactElement<any>, {
        key: childKey,
        animationOrder: index,
        animationState,
      })
    })
  }, [children, enableTooltipItemAnimations])

  // Actualizar tracking refs después de render
  useEffect(() => {
    previousChildrenKeysRef.current = currentChildrenKeys
    previousTitleRef.current = title

    // Marcar que ya pasó el primer render
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false
    }

    if (titleChanged) {
      setIsTransitioning(true)
      const timer = setTimeout(() => setIsTransitioning(false), 250)
      return () => clearTimeout(timer)
    }
  }, [currentChildrenKeys, title, titleChanged])

  // Generar liquid glass effect (solo si está habilitado)
  const liquidGlassData = enableLiquidGlass
    ? useSidebarLiquidGlass('tooltip', { enableChromaticAberration })
    : null

  useEffect(() => {
    if (!tooltipRef.current) return

    const animation = tooltipRef.current.animate(
      [
        { opacity: 0, transform: 'translateX(-5px) scale(0.98)' },
        { opacity: 1, transform: 'translateX(0) scale(1)' }
      ],
      {
        duration: 150,
        easing: 'ease-out',
        fill: 'forwards'
      }
    )

    return () => {
      animation.cancel()
      if (tooltipRef.current) {
        tooltipRef.current.style.opacity = '0'
      }
    }
  }, [])

  const anchorStyles: React.CSSProperties = {
    position: "absolute",
    positionAnchor: "--sidebar-indicator",
    left: "anchor(right)",
    marginLeft: "calc(var(--sidebar-width) + var(--sidebar-tooltip-distance))",
    top: "anchor(center)",
    translate: "0 -50%",
    zIndex: 50,
  }

  return (
    <>
      {/* SVG Filters (solo si liquid glass está habilitado) */}
      {enableLiquidGlass && liquidGlassData && (
        <svg className="absolute inset-0 pointer-events-none w-0 h-0" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <filter id="liquid-glass-tooltip" colorInterpolationFilters="sRGB">
              <feImage
                x="0"
                y="0"
                width="100%"
                height="100%"
                result="map"
                href={liquidGlassData.dataUri}
              />
              <feDisplacementMap
                in="SourceGraphic"
                in2="map"
                xChannelSelector={liquidGlassData.filterAttributes.red.xChannelSelector}
                yChannelSelector={liquidGlassData.filterAttributes.red.yChannelSelector}
                scale={liquidGlassData.filterAttributes.red.scale}
                result="displaced"
              />
              <feGaussianBlur
                in="displaced"
                stdDeviation={liquidGlassData.filterAttributes.gaussianBlur.stdDeviation}
              />
            </filter>

            {enableBackdropExtension && (
              <mask id="tooltip-backdrop-mask">
                <rect x="0" y="0" width="100%" height="100%" fill="white" />
              </mask>
            )}
          </defs>
        </svg>
      )}

      {/* Safety Triangle CSS - Pure CSS hover intent detection */}
      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes activatePointerEvents {
            to { pointer-events: all; }
          }

          .sidebar-tooltip::before {
            content: '';
            position: absolute;
            right: 100%;
            top: 0;
            width: calc(var(--sidebar-tooltip-distance) + 0.25rem + 2px);
            height: 100%;
            clip-path: polygon(0% 25%, 100% 0%, 100% 100%, 0% 75%);
            pointer-events: none;
            animation: activatePointerEvents 0s 0.15s forwards;
            z-index: -1;
            scale: 1;
            transition: scale 0s 0.2s;
            ${debug ? `
              background: repeating-linear-gradient(
                45deg,
                transparent,
                transparent 10px,
                rgba(244, 114, 182, 0.15) 10px,
                rgba(244, 114, 182, 0.15) 20px
              );
              border: 2px dashed rgb(244 114 182);
            ` : ''}
          }
        `
      }} />

      {/* Debug label para safety triangle */}
      {debug && (
        <div
          className="absolute text-[9px] text-pink-300 font-mono bg-black/70 px-1.5 py-0.5 rounded pointer-events-none z-50"
          style={{
            right: "calc(100% + 4px)",
            top: "50%",
            transform: "translateY(-50%)",
          }}
        >
          SAFETY TRIANGLE
        </div>
      )}

      <div
        ref={tooltipRef}
        className="sidebar-tooltip min-w-56 opacity-0"
        style={anchorStyles}
        onMouseLeave={onMouseLeave}
      >
        {/* Backdrop Extension Layer (solo si está habilitado) */}
        {enableLiquidGlass && enableBackdropExtension && (
          <div
            className="absolute inset-0 rounded-lg pointer-events-none"
            style={{
              height: '200%',
              top: '-50%',
              mask: 'url(#tooltip-backdrop-mask)',
              WebkitMask: 'url(#tooltip-backdrop-mask)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              opacity: 0.6,
              zIndex: 0
            }}
          />
        )}

        {/* Inner container */}
        <div
          className={cn(
            "sidebar-tooltip-inner overflow-hidden rounded-lg shadow-lg border border-border relative",
            enableLiquidGlass ? "" : "bg-card backdrop-blur-sm",
            debug && enableEdgeGlass3D && "ring-2 ring-cyan-400/50"
          )}
          style={enableLiquidGlass ? {
            background: 'rgba(24, 24, 27, var(--tooltip-frost, 0.05))',
            backdropFilter: `blur(${liquidGlassData?.filterAttributes?.gaussianBlur?.stdDeviation || 8}px) url(#liquid-glass-tooltip) brightness(1.1) saturate(1.5)`,
            WebkitBackdropFilter: `blur(${liquidGlassData?.filterAttributes?.gaussianBlur?.stdDeviation || 8}px) url(#liquid-glass-tooltip) brightness(1.1) saturate(1.5)`,
            willChange: 'backdrop-filter, transform',
            transform: 'translateZ(0)'
          } : undefined}
        >
          {/* Edge Glass 3D Layers (solo si está habilitado) */}
          {enableLiquidGlass && enableEdgeGlass3D && (
            <>
              {/* Depth layer */}
              <div
                className="absolute inset-0 rounded-lg pointer-events-none"
                style={{
                  backdropFilter: 'blur(12px) url(#liquid-glass-tooltip) brightness(1.1) saturate(1.3)',
                  WebkitBackdropFilter: 'blur(12px) url(#liquid-glass-tooltip) brightness(1.1) saturate(1.3)',
                  background: 'rgba(24, 24, 27, 0.3)',
                  zIndex: 1,
                  opacity: debug ? 0.7 : 1,
                  willChange: 'backdrop-filter',
                  transform: 'translateZ(0)'
                }}
              >
                {debug && (
                  <div className="absolute top-1 left-1 text-[8px] text-cyan-300 font-mono bg-black/70 px-1 rounded">
                    DEPTH
                  </div>
                )}
              </div>

              {/* Surface layer */}
              <div
                className="absolute inset-0 rounded-lg pointer-events-none"
                style={{
                  backdropFilter: 'blur(6px) url(#liquid-glass-tooltip) brightness(1.15) saturate(1.4)',
                  WebkitBackdropFilter: 'blur(6px) url(#liquid-glass-tooltip) brightness(1.15) saturate(1.4)',
                  background: 'rgba(24, 24, 27, 0.5)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  zIndex: 2,
                  opacity: debug ? 0.7 : 1,
                  willChange: 'backdrop-filter',
                  transform: 'translateZ(0)'
                }}
              >
                {debug && (
                  <div className="absolute top-1 right-1 text-[8px] text-purple-300 font-mono bg-black/70 px-1 rounded">
                    SURFACE
                  </div>
                )}
              </div>
            </>
          )}

          {/* Glow effects */}
          <div className="absolute -left-1 -top-6 h-12 w-12 rounded-full bg-primary/10 blur-xl pointer-events-none z-0" />
          <div className="absolute -right-1 -bottom-6 h-12 w-12 rounded-full bg-primary/10 blur-xl pointer-events-none z-0" />

          {/* Content layer */}
          <div className="sidebar-tooltip-content relative pointer-events-auto" style={{ zIndex: 10 }}>
            {/* Title Header */}
            {title && (
              <div className="flex items-center px-4 py-3 font-medium border-b border-border/50 text-foreground relative">
                {enableCylinderTitle ? (
                  <TooltipTitleCylinder
                    title={title}
                    direction={currentDirection}
                  />
                ) : (
                  <span>{title}</span>
                )}

                {debug && (
                  <span className="ml-auto text-[8px] text-purple-400 font-mono space-x-1">
                    {enableLiquidGlass && <span>GLASS</span>}
                    {enableEdgeGlass3D && <span>3D</span>}
                    {enableChromaticAberration && <span>CA</span>}
                    {enableTooltipItemAnimations && <span>ITEMS</span>}
                    {enableCylinderTitle && <span>CYLINDER</span>}
                  </span>
                )}
              </div>
            )}

            {/* Content con grid trick para height transitions */}
            <div
              ref={contentGridRef}
              className="tooltip-content-grid"
              data-state="open"
            >
              <div className="tooltip-content-inner">
                <div className="max-h-[calc(100vh-200px)] overflow-y-auto py-2">
                  <div className="space-y-0.5 px-2">
                    {processedChildren}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
})
